import { call, put, takeEvery, select, delay } from 'redux-saga/effects';
import { getAllMarketData, getMarketData } from '../../services/allApiService';
// Used to skip this REST poll while the Socket.IO bridge is delivering
// ticks (see marketSocketSaga.js). When the socket is down this poll
// runs exactly as before, so behavior is unchanged.
import { isMarketSocketConnected, waitForConnection } from '../../services/socket/marketSocket';
import {
  fetchSymbolTokensRequest,
  fetchSymbolTokensSuccess,
  fetchSymbolTokensFailure,
  fetchMarketDataRequest,
  fetchMarketDataSuccess,
  fetchMarketDataFailure,
  updateMarketData,
  clearMarketData,
} from '../reducers/marketReducer';
import { getClientData } from '../../components/custom';
import { take, fork, cancel, takeLeading, takeLatest } from 'redux-saga/effects';

function* handleFetchSymbolTokens(action) {
  const userData = getClientData();
  const setting = userData?.parent?.setting || {};
  const { marketDataOld } = yield select((state) => state.market); // Get cached market data
  const { tab, list } = action.payload;

  // Clear the currently shown cards immediately so a tab switch (e.g. NFO → MCX)
  // never leaves the previous exchange's scripts on screen while the new tab
  // loads. fetchSymbolTokensRequest already set loading=true → shows shimmer.
  yield put(updateMarketData([]));

  try {
    let marketTabs = [];
    if (tab === 'NFO') {
      if (setting.equity_trading === '1') {
        marketTabs.push('EQUITY');
      }
      if (setting.equity_index_trading === '1') {
        marketTabs.push('EQUITY(INDEX)');
      }
    } else {
      marketTabs = [tab];
    }

    let symbolTokensArray = [];

    for (const marketTab of marketTabs) {
      // Check if data for the tab is already cached
      if (marketDataOld[marketTab]) {
        const tabToKeyMap = {
          'Top Gainers': 'top_gainers',
          'Top Losers': 'top_losers',
        };
        const key = tabToKeyMap[list];
        if (marketDataOld[marketTab][key]) {
          const tokens = marketDataOld[marketTab][key].map((item) => ({
            script_id: item.script_id,
            token: item.token,
            exchange_id: item.exchange_id,
            lots: item.lots || null,
            symbol: item.symbol || null,
            script_name: item.script_name || null,
          }));
          symbolTokensArray = [...symbolTokensArray, ...tokens];
        }
      } else {
        // Fetch data from API if not cached
        const response = yield call(getAllMarketData, marketTab, userData?.client_id);

        const tabToKeyMap = {
          'Top Gainers': 'top_gainers',
          'Top Losers': 'top_losers',
        };
        const key = tabToKeyMap[list];

        if (response[marketTab] && response[marketTab][key]) {
          const tokens = response[marketTab][key].map((item) => ({
            script_id: item.script_id,
            token: item.token,
            exchange_id: item.exchange_id,
            lots: item.lots || null,
            symbol: item.symbol || null,
            script_name: item.script_name || null,
          }));
          symbolTokensArray = [...symbolTokensArray, ...tokens];
        }

        // Cache the response in the Redux store
        yield put(fetchSymbolTokensSuccess({ tokens: symbolTokensArray, tab: marketTab, data: response[marketTab] }));
      }
    }

    // Only dispatch success if tokens were found or fetched
    if (symbolTokensArray.length > 0) {
      yield put(fetchSymbolTokensSuccess({ tokens: symbolTokensArray, tab, data: marketDataOld[tab] }));
      yield put(fetchMarketDataRequest({ tab, symbolTokensArray }));
    } else {
      yield put(fetchSymbolTokensFailure('No tokens found for the selected tab'));
    }
  } catch (error) {
    yield put(fetchSymbolTokensFailure(error.message));
  }
}

function* handleFetchMarketData(action) {
  try {
    const { tab, symbolTokensArray } = action.payload;

    const tokens = symbolTokensArray.map(item => item.token);

    if (tokens.length === 0) return;

    // ── ONE-TIME snapshot: do a single REST quote on load so the LAST traded
    // price shows immediately — even when the market is closed and the socket
    // has no live ticks (e.g. NFO after 3:30 PM). The socket then updates prices
    // live; the repeating REST poll stays disabled while the socket is connected
    // (see handleUpdateMarketData). If the snapshot fails (e.g. rate limit), we
    // fall back to seeding names from the list so cards still render.
    const mode = 'FULL';
    const exchangeTokens = { [tab]: tokens };

    let response;
    try {
      response = yield call(getMarketData, mode, exchangeTokens);
    } catch (snapErr) {
      const seeded = symbolTokensArray
        .map(st => ({
          symbolToken: String(st.token),
          tradingSymbol: st.symbol || st.script_name || '',
          exchange: tab,
          script_id: st.script_id ?? null,
          exchange_id: st.exchange_id ?? null,
          lots: st.lots ?? null,
          ltp: 0, high: 0, low: 0, open: 0, close: 0,
          avgPrice: 0, tradeVolume: 0, netChange: 0, percentChange: 0,
          depth: { buy: [], sell: [] },
        }))
        .sort((a, b) =>
          String(a.tradingSymbol || '').localeCompare(String(b.tradingSymbol || ''))
        );
      yield put(fetchMarketDataSuccess(seeded));
      return;
    }

    if (response && response.data && response.data.fetched) {
      const data = response.data.fetched.sort((a, b) =>
        a.tradingSymbol.localeCompare(b.tradingSymbol)
      );

      const sortedData = data.map(item => {
        const matched = symbolTokensArray.find(
          entry => String(entry.token) === String(item.symbolToken)
        );
        return {
          ...item,
          script_id: matched?.script_id || null,
          exchange_id: matched?.exchange_id || null,
          watchlist_id: matched?.id || null,
          lots: matched?.lots || null,

        };
      });
      yield put(fetchMarketDataSuccess(sortedData));
      localStorage.setItem("marketData", JSON.stringify(sortedData));

    } else {
      yield put(fetchMarketDataFailure('Invalid response format'));
    }
  } catch (error) {
    yield put(fetchMarketDataFailure(error.message));
  }
}

function* handleUpdateMarketData() {
  let isFetching = false;
  let firstRun = true;
  while (true) {
    if (!isFetching) {
      try {
        // On the first iteration, wait out the socket connect window so we
        // don't fire a quote while the socket is still connecting.
        let socketUp;
        if (firstRun) { socketUp = yield call(waitForConnection); firstRun = false; }
        else { socketUp = isMarketSocketConnected(); }
        if (socketUp) {
          yield delay(1000);
          continue;
        }

        const { activeTab, symbolTokens } = yield select(state => state.market);

        if (symbolTokens && symbolTokens.length > 0) {

          const tokens = symbolTokens.map(item => item.token);
          const mode = 'FULL';
          const exchangeTokens = { [activeTab]: tokens };
          const response = yield call(getMarketData, mode, exchangeTokens);

          if (response && response.data && response.data.fetched) {

            const data = response.data.fetched.sort((a, b) =>
              a.tradingSymbol.localeCompare(b.tradingSymbol)
            );

            const sortedData = data.map(item => {

              const matched = symbolTokens.find(entry => String(entry.token) === String(item.symbolToken));

              return {
                ...item,
                script_id: matched?.script_id || null,
                exchange_id: matched?.exchange_id || null,
                lots: matched?.lots || null,
              };
            });

            yield put(updateMarketData(sortedData));
          }
        }

      } catch (error) {
        console.error('Error updating market data:', error);
        yield delay(1000);
      }
      finally {
        isFetching = false;
      }
    }
    yield delay(1000);
  }
}
// function* handleStopMarketDataUpdates() {
//   try {
//     // Clear all market data when stopping updates
//     yield put(clearMarketData());
//   } catch (error) {
//     console.error('Error stopping market data updates:', error);
//   }
// }

export default function* marketSaga() {
  // takeLatest (not takeLeading) so switching tabs (NFO ⇄ MCX) cancels the
  // in-flight request and the LATEST tab's data wins — otherwise a slower
  // previous fetch could leave the wrong exchange's cards on screen.
  yield takeLatest(fetchSymbolTokensRequest.type, handleFetchSymbolTokens);
  yield takeLatest(fetchMarketDataRequest.type, handleFetchMarketData);
  // yield takeEvery('STOP_MARKET_DATA_UPDATES', handleStopMarketDataUpdates);


  while (true) {
    yield take('START_MARKET_DATA_UPDATES');
    const task = yield fork(handleUpdateMarketData);
    yield take('STOP_MARKET_DATA_UPDATES');
    yield cancel(task);
  }
}